/**
 * The Framing control of the Generate panel (spec 027 D-22): four icon
 * buttons, each a figure silhouette cut at that height above its label.
 * Pressing one applies `setPictureFraming` for the subject (one story
 * history entry, through the parent); every expression shares it. Shown
 * only on the character's master picture (D-33); once the character has a
 * picture the parent confirms first, since the change resets it.
 */
import React from 'react';
import type { CharacterFraming } from '../../../../Shared/src/@types';
import { CHARACTER_FRAMINGS, FRAMING_LABELS } from '../../../../Shared/src/pictures';
import { BRIEF_TEXT } from './imageGenText';
import styles from './ImageGen.module.scss';

/** Where each framing cuts the 24 × 32 silhouette. */
const CUT: Readonly<Record<CharacterFraming, number>> = { head: 15, half: 21, knees: 27, full: 32 };

const Silhouette: React.FC<{ framing: CharacterFraming }> = ({ framing }) => {
  const id = `framing-cut-${framing}`;
  return (
    <svg viewBox="0 0 24 32" width="24" height="32" aria-hidden="true" focusable="false">
      <defs>
        <clipPath id={id}><rect x="0" y="0" width="24" height={CUT[framing]} /></clipPath>
      </defs>
      <rect x="0.5" y="0.5" width="23" height="31" rx="2" fill="none" stroke="currentColor" strokeOpacity="0.25" />
      <g clipPath={`url(#${id})`} fill="currentColor">
        <circle cx="12" cy="7" r="3.6" />
        <path d="M5.5 15.5c0-2.5 2.6-4.3 6.5-4.3s6.5 1.8 6.5 4.3V22h-2v9.5h-3.2V23h-2.6v8.5H7.5V22h-2z" />
      </g>
      {framing !== 'full' && <line x1="2" x2="22" y1={CUT[framing]} y2={CUT[framing]} stroke="currentColor" strokeDasharray="2 1.5" strokeOpacity="0.6" />}
    </svg>
  );
};

export interface FramingControlProps {
  subject: string;
  value: CharacterFraming;
  onChange: (framing: CharacterFraming) => void;
  /** The character has pictures: a change resets it (027 D-33). */
  resets?: boolean;
  disabled?: boolean;
}

export const FramingControl: React.FC<FramingControlProps> = ({ subject, value, onChange, resets, disabled }) => (
  <div className={styles.framing} data-testid="generate-framing">
    <span className={styles.fieldLabel} id="generate-framing-label">{BRIEF_TEXT.framing}</span>
    <div className={styles.framingButtons} role="group" aria-labelledby="generate-framing-label">
      {CHARACTER_FRAMINGS.map((f) => (
        <button
          key={f}
          type="button"
          className={`${styles.framingButton} ${f === value ? styles.framingPicked : ''}`}
          aria-pressed={f === value}
          data-framing={f}
          disabled={disabled}
          onClick={() => { if (f !== value) onChange(f); }}
        >
          <Silhouette framing={f} />
          <span>{FRAMING_LABELS[f]}</span>
        </button>
      ))}
    </div>
    <p className={styles.muted} data-testid="generate-framing-note">
      {BRIEF_TEXT.framingNote(subject)}{resets ? BRIEF_TEXT.framingResets(subject) : ''}
    </p>
  </div>
);

export default FramingControl;
